import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Package, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabCount } from '@/components/common/TabCount';
import { toast } from 'sonner';
import { PageHeader, LoadingState, DataTable, type Column } from '@/components/common';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import {
  forecastingApi,
  type DemandForecast,
  type ForecastPeriod,
  type HistoricalDemand,
} from '@/api/services/forecasting.api';
import { formatDate, formatNumber, toTitleCase } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';

/**
 * One demand forecast, its periods and the history it was built from.
 *
 * A forecast has a lifecycle — generated, activated, archived — and gets cited
 * in planning discussions. It lived in a sheet, so there was no way to send
 * anyone "the forecast we are working to".
 */
export default function ForecastDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [forecast, setForecast] = useState<DemandForecast | null>(null);
  const [historical, setHistorical] = useState<HistoricalDemand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await forecastingApi.getById(id);
        if (cancelled) return;
        setForecast(res.data);

        // The history the forecast was fitted against; a failure here should
        // not cost the reader the forecast itself.
        const hist = await forecastingApi
          .getHistoricalDemand(res.data.itemId, {
            locationId: res.data.locationId || undefined,
            limit: 24,
          })
          .catch(() => ({ data: [] as HistoricalDemand[] }));
        if (!cancelled) setHistorical(hist.data);
      } catch (error) {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/forecasting');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!forecast) return null;

  const periodColumns: Column<ForecastPeriod>[] = [
    {
      key: 'periodStart',
      header: 'Period',
      sortable: true,
      render: (p) => (
        <span className="whitespace-nowrap">
          {formatDate(p.periodStart)} – {formatDate(p.periodEnd)}
        </span>
      ),
    },
    {
      key: 'predictedDemand',
      header: 'Predicted',
      sortable: true,
      render: (p) => <span className="tabular-nums">{formatNumber(p.predictedDemand)}</span>,
    },
    {
      key: 'actualDemand',
      header: 'Actual',
      sortable: true,
      render: (p) => (
        <span className="tabular-nums">
          {p.actualDemand != null ? formatNumber(p.actualDemand) : '—'}
        </span>
      ),
    },
    {
      key: 'lowerBound',
      header: 'Range',
      render: (p) => (
        <span className="tabular-nums text-muted-foreground">
          {p.lowerBound != null && p.upperBound != null
            ? `${formatNumber(p.lowerBound)} – ${formatNumber(p.upperBound)}`
            : '—'}
        </span>
      ),
    },
    {
      key: 'variance',
      header: 'Variance',
      sortable: true,
      render: (p) => {
        if (p.actualDemand == null) return <span className="text-muted-foreground">—</span>;
        const diff = p.actualDemand - p.predictedDemand;
        const pct = p.predictedDemand ? (diff / p.predictedDemand) * 100 : 0;
        return (
          // The sign is the point: over-forecasting and under-forecasting cost
          // different things, and a bare magnitude hides which happened.
          <span
            className={
              Math.abs(pct) < 10
                ? 'tabular-nums'
                : pct > 0
                  ? 'tabular-nums text-status-warning-foreground'
                  : 'tabular-nums text-status-destructive-foreground'
            }
          >
            {pct > 0 ? '+' : ''}
            {pct.toFixed(1)}%
          </span>
        );
      },
    },
  ];

  const historicalColumns: Column<HistoricalDemand>[] = [
    {
      key: 'periodDate',
      header: 'Date',
      sortable: true,
      render: (h) => formatDate(h.periodDate),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      sortable: true,
      render: (h) => <span className="tabular-nums">{formatNumber(h.quantity)}</span>,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Forecasting', href: '/forecasting' },
            { label: forecast.item?.sku ?? 'Forecast' },
          ]}
          title={forecast.item?.name ?? 'Demand forecast'}
          description={forecast.item?.sku}
          titleAdornment={<Badge variant="outline">{toTitleCase(forecast.status)}</Badge>}
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard title="Item" icon={<Package className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    {
                      label: 'SKU',
                      value: forecast.item ? (
                        <Link
                          to={`/inventory/${forecast.itemId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {forecast.item.sku}
                        </Link>
                      ) : (
                        '—'
                      ),
                    },
                    {
                      label: 'Location',
                      value: forecast.location?.name,
                      when: !!forecast.location?.name,
                    },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Method and accuracy"
                icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Method', value: toTitleCase(forecast.forecastMethod) },
                    {
                      label: 'Confidence',
                      value: forecast.confidence != null ? `${forecast.confidence}%` : '—',
                    },
                    {
                      label: 'MAPE',
                      value: forecast.mape != null ? `${forecast.mape.toFixed(2)}%` : '—',
                      // Mean absolute percentage error: the lower the better, and
                      // past about a fifth the forecast is not worth planning to.
                      tone:
                        forecast.mape == null
                          ? 'default'
                          : forecast.mape > 20
                            ? 'destructive'
                            : forecast.mape > 10
                              ? 'warning'
                              : 'success',
                    },
                    { label: 'Periods', value: forecast.periods?.length ?? 0 },
                    {
                      label: 'History used',
                      value: `${historical.length} period${historical.length === 1 ? '' : 's'}`,
                    },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <Card>
              <CardHeader>
                <h3 className="text-base font-semibold text-foreground">Forecast</h3>
                <CardDescription>
                  Predicted demand by period, and the history it was fitted against
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="periods">
                  <TabsList>
                    <TabsTrigger value="periods">
                      Periods
                      <TabCount value={forecast.periods?.length ?? 0} />
                    </TabsTrigger>
                    <TabsTrigger value="historical">
                      Historical demand
                      <TabCount value={historical.length} />
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="periods">
                    <DataTable<ForecastPeriod>
                      pagination
                      data={forecast.periods ?? []}
                      columns={periodColumns}
                      emptyTitle="No periods"
                      emptyDescription="This forecast has no periods to show."
                    />
                  </TabsContent>

                  <TabsContent value="historical">
                    <DataTable<HistoricalDemand>
                      pagination
                      data={historical}
                      columns={historicalColumns}
                      emptyTitle="No historical demand"
                      emptyDescription="Nothing has been recorded for this item and location yet."
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          }
        />
      </motion.div>
    </motion.main>
  );
}
